import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getReferralSource, logAnalyticsEvent } from '@/lib/referral';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Helper function to detect mobile device
 * Mobile Safari often has issues with cross-origin cookies
 */
const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status - uses XMLHttpRequest to avoid platform fetch interception
  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        setUser(null);
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      // Use XMLHttpRequest to avoid platform fetch interception bug.
      // `status` is 0 on network failure / abort / timeout — we
      // surface that distinctly so the caller can decide between
      // "token is bad" (a definitive 401/403) and "we couldn't
      // reach the server" (transient). The previous implementation
      // collapsed both into a single "ok: false" branch and wiped
      // the token on every transient failure, which kicked Casey
      // out of Outreach every time the backend hot-reloaded.
      const result = await new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${API}/auth/me`, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        // Bumped from 10s → 25s: a 10s ceiling was too aggressive
        // for users on slower networks (mobile / hotspot / VPN).
        // When the request occasionally took >10s the XHR aborted,
        // we logged the user out, and they had to sign in again
        // mid-task. /auth/me is a single-query endpoint that
        // typically responds in <300ms; 25s is purely a network
        // safety net.
        xhr.timeout = 25000;

        xhr.onload = function() {
          try {
            if (xhr.status >= 200 && xhr.status < 300) {
              const userData = JSON.parse(xhr.responseText);
              resolve({ outcome: 'authenticated', data: userData });
            } else if (xhr.status === 401 || xhr.status === 403) {
              // Server explicitly rejected the token — safe to wipe.
              resolve({ outcome: 'invalid_token' });
            } else {
              // 5xx, weird non-JSON, etc. — server reachable but
              // refused to confirm or deny. Don't punish the user.
              resolve({ outcome: 'transient' });
            }
          } catch (e) {
            resolve({ outcome: 'transient' });
          }
        };

        // Network errors / timeouts / aborts → transient.
        // We KEEP the token and KEEP the cached user from
        // sessionStorage (set below on prior success) so the page
        // doesn't bounce to /login during a backend hiccup.
        xhr.onerror = function() { resolve({ outcome: 'transient' }); };
        xhr.ontimeout = function() { resolve({ outcome: 'transient' }); };

        xhr.send();
      });

      if (result.outcome === 'authenticated') {
        setUser(result.data);
        setIsAuthenticated(true);
        // Cache user data in sessionStorage for faster access checks
        sessionStorage.setItem('cached_user', JSON.stringify(result.data));
      } else if (result.outcome === 'invalid_token') {
        // Definitive server-side rejection → clear everything.
        localStorage.removeItem('token');
        sessionStorage.removeItem('cached_user');
        setUser(null);
        setIsAuthenticated(false);
      } else {
        // Transient failure. Keep the token. Re-hydrate from the
        // cached user if we have one so protected routes don't
        // flicker to "Sign In" while the network recovers.
        const cached = sessionStorage.getItem('cached_user');
        if (cached) {
          try {
            const u = JSON.parse(cached);
            setUser(u);
            setIsAuthenticated(true);
          } catch (e) {
            // cache corrupted; treat as unauthenticated for this
            // session ONLY — don't wipe the token, the next
            // checkAuth attempt will recover.
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          // First load + transient backend failure → can't render
          // protected UI yet, but DO NOT wipe the token. Next
          // checkAuth (e.g. on next route mount or user action)
          // will try again.
          setUser(null);
          setIsAuthenticated(false);
        }
      }
    } catch (error) {
      // Top-level catch — also transient. Keep token, fall back to
      // cached user if available.
      console.error('Auth check failed:', error);
      const cached = sessionStorage.getItem('cached_user');
      if (cached) {
        try {
          setUser(JSON.parse(cached));
          setIsAuthenticated(true);
        } catch (e) {
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Register with email/password - uses XMLHttpRequest to avoid fetch interception issues
  const register = async (email, password, name, options = {}, _attempt = 0) => {
    return new Promise((resolve, reject) => {
      const referralSource = getReferralSource();
      const payload = { email, password, name };
      if (referralSource) payload.referral_source = referralSource;
      
      // Add notification preferences if provided
      if (options.notifyUpdates !== undefined) {
        payload.notify_case_updates = options.notifyUpdates;
      }
      
      console.log('Attempting registration to:', `${API}/auth/register`);

      // One-shot retry helper. Triggers ONLY on transient infra failures
      // (HTTP 5xx or non-JSON responses from CDN error pages, e.g. Cloudflare
      // 520). 4xx, 2xx, network errors, and timeouts pass through unchanged.
      const retryOnceOrFail = (errMsg) => {
        if (_attempt === 0) {
          setTimeout(() => {
            register(email, password, name, options, 1).then(resolve).catch(reject);
          }, 600);
        } else {
          reject(new Error(errMsg));
        }
      };

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/auth/register`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.withCredentials = true;
      xhr.timeout = 30000;
      
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          
          if (xhr.status >= 200 && xhr.status < 300) {
            // Success - store token and update state
            if (data.access_token) {
              localStorage.setItem('token', data.access_token);
            }
            setUser(data.user);
            setIsAuthenticated(true);
            // Fire signup analytics event (non-blocking) so we can attribute
            // the signup back to whichever ?ref= source brought them in.
            logAnalyticsEvent('signup', {
              user_id: data.user?.user_id,
              auth_provider: 'email',
            });
            resolve(data);
          } else if (xhr.status >= 500) {
            retryOnceOrFail(data.detail || 'Server temporarily unavailable. Please try again.');
          } else {
            // Handle specific error cases
            if (data.detail === 'Email already registered') {
              reject(new Error('This email is already registered. Please sign in instead.'));
            } else {
              reject(new Error(data.detail || 'Registration failed'));
            }
          }
        } catch (parseError) {
          console.error('Failed to parse response:', parseError, 'Response:', xhr.responseText);
          retryOnceOrFail('Server returned invalid response. Please try again.');
        }
      };
      
      xhr.onerror = function() {
        console.error('Registration network error');
        reject(new Error('Unable to connect to server. Please check your internet connection.'));
      };
      
      xhr.ontimeout = function() {
        console.error('Registration timeout');
        reject(new Error('Request timed out. Please check your connection and try again.'));
      };
      
      xhr.send(JSON.stringify(payload));
    });
  };

  // Login with email/password - uses XMLHttpRequest to avoid fetch interception issues
  const login = async (email, password, _attempt = 0) => {
    const isMobile = isMobileDevice();
    
    return new Promise((resolve, reject) => {
      console.log('Attempting login to:', `${API}/auth/login`, isMobile ? '(mobile)' : '(desktop)');

      // One-shot retry helper. Triggers ONLY on transient infra failures
      // (HTTP 5xx or non-JSON responses from CDN error pages, e.g. Cloudflare
      // 520). 4xx, 2xx, network errors, and timeouts pass through unchanged.
      const retryOnceOrFail = (errMsg) => {
        if (_attempt === 0) {
          setTimeout(() => {
            login(email, password, 1).then(resolve).catch(reject);
          }, 600);
        } else {
          reject(new Error(errMsg));
        }
      };

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/auth/login`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.withCredentials = true;
      xhr.timeout = 30000;
      
      xhr.onload = function() {
        console.log('Login response status:', xhr.status);
        
        try {
          const data = JSON.parse(xhr.responseText);
          
          if (xhr.status >= 200 && xhr.status < 300) {
            // Store token in localStorage (critical for mobile where cookies may fail)
            if (data.access_token) {
              localStorage.setItem('token', data.access_token);
              console.log('Token stored in localStorage');
            } else {
              console.warn('No access token in response');
            }
            
            // Cache user data in sessionStorage for faster access checks
            if (data.user) {
              sessionStorage.setItem('cached_user', JSON.stringify(data.user));
            }
            
            setUser(data.user);
            setIsAuthenticated(true);
            console.log('Login successful, user:', data.user?.email);
            resolve(data);
          } else if (xhr.status >= 500) {
            retryOnceOrFail(data.detail || 'Server temporarily unavailable. Please try again.');
          } else {
            reject(new Error(data.detail || 'Login failed'));
          }
        } catch (parseError) {
          console.error('Failed to parse login response:', parseError);
          retryOnceOrFail('Server returned invalid response. Please try again.');
        }
      };
      
      xhr.onerror = function() {
        console.error('Login network error', isMobile ? '(mobile)' : '(desktop)');
        reject(new Error('Unable to connect to server. Please check your internet connection.'));
      };
      
      xhr.ontimeout = function() {
        console.error('Login timeout', isMobile ? '(mobile)' : '(desktop)');
        reject(new Error('Connection timed out. Please check your internet and try again.'));
      };
      
      xhr.send(JSON.stringify({ email, password }));
    });
  };

  // Process Google OAuth session
  const processGoogleSession = async (sessionId) => {
    try {
      // Forward the localStorage-stashed referral source so the OAuth
      // signup path inherits the same `?ref=reddit` attribution as the
      // email signup path. Backend also has a Referer fallback.
      const ref = getReferralSource();
      const url = ref
        ? `${API}/auth/session?session_id=${encodeURIComponent(sessionId)}&referral_source=${encodeURIComponent(ref)}`
        : `${API}/auth/session?session_id=${encodeURIComponent(sessionId)}`;
      const response = await fetch(url, {
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Google authentication failed');
      }

      // Store token in localStorage as fallback for cookie-based auth
      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
      }

      setUser(data.user);
      setIsAuthenticated(true);

      // Fire signup analytics event (non-blocking) so we can attribute
      // Google-OAuth signups back to whichever ?ref= source brought them
      // in. Mirror what the email-register flow does.
      logAnalyticsEvent('signup', {
        user_id: data.user?.user_id,
        auth_provider: 'google',
      });

      return data;
    } catch (error) {
      if (error.name === 'TypeError' || error.message.includes('JSON')) {
        throw new Error('Unable to connect to server. Please try again.');
      }
      throw error;
    }
  };

  // Logout
  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear token from localStorage
      localStorage.removeItem('token');
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // Start Google OAuth flow
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    register,
    login,
    logout,
    loginWithGoogle,
    processGoogleSession,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
